<template>
  <div class="p-4 flex gap-2">
    <div>
      <Select v-model="paymentStatus" @update:modelValue="onUpdatePaymentStatus($event)">
        <SelectTrigger>
          <SelectValue :value="paymentStatus" class="font-medium" placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Payment Status</SelectLabel>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="false">Due</SelectItem>
            <SelectItem value="true">Paid</SelectItem>
            <SelectItem value="SETTLED">Settled</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
            <SelectItem value="MISSED">Missed</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Select v-model="paymentType" @update:modelValue="onUpdatePaymentType($event as PaymentType)">
        <SelectTrigger>
          <SelectValue :value="paymentType" class="font-medium" placeholder="Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Type</SelectLabel>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="DEPOSIT">Deposit</SelectItem>
            <SelectItem value="CONTRIBUTION">Contribution</SelectItem>
            <SelectItem value="REFUND">Refund</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Select v-model="paymentMethod" @update:modelValue="onUpdatePaymentMethod($event as PaymentMethod | 'ALL')">
        <SelectTrigger>
          <SelectValue :value="paymentMethod" class="font-medium" placeholder="Method" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Method</SelectLabel>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="CARD">Card</SelectItem>
            <SelectItem value="DEBICHECK">Debicheck</SelectItem>
            <SelectItem value="EFT">EFT</SelectItem>
            <SelectItem value="OFFLINE">Offline</SelectItem>
            <SelectItem value="TRANSFER">Transfer</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>

    <div>
      <Popover>
        <PopoverTrigger as-child>
          <Button
            variant="outline"
            :class="cn('w-[280px] justify-start text-left font-normal', !startEndDates && 'text-muted-foreground')"
          >
            <CalendarIcon class="mr-2 h-4 w-4" />
            <template v-if="startEndDates.start">
              <template v-if="startEndDates.end">
                <span class="font-medium">
                  {{ df.format(startEndDates.start.toDate(getLocalTimeZone())) }} -
                  {{ df.format(startEndDates.end.toDate(getLocalTimeZone())) }}
                </span>
              </template>

              <template v-else>
                <span class="font-medium"></span>
                {{ df.format(startEndDates.start.toDate(getLocalTimeZone())) }}
              </template>
            </template>
            <template v-else>
              <span class="font-medium">Select Dates</span>
            </template>
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0">
          <RangeCalendar
            v-model="startEndDates"
            initial-focus
            :number-of-months="2"
            @update:modelValue="onDateChange"
          />
        </PopoverContent>
      </Popover>
    </div>
    <div>
      <Button variant="outline" type="button" @click="onClearFilters">
        <CircleX class="h-4 w-4" />
        Clear
      </Button>
    </div>
    <div class="w-full max-w-[350px] ml-auto">
      <div class="relative">
        <Search class="h-4 w-4 font-bold text-grey-light-500 absolute left-2 top-1/2 transform -translate-y-1/2" />
        <Input v-model="searchTerm" placeholder="Search" class="pl-8" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RangeCalendar } from '@/components/ui/range-calendar';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { PaymentMethod, PaymentStatus, PaymentType } from '@/models/payments';
import { debounce } from '@/utils/lowDash';
import { DateFormatter, getLocalTimeZone } from '@internationalized/date';
import { Calendar as CalendarIcon, CircleX, Search } from 'lucide-vue-next';
import type { DateRange } from 'radix-vue';
import { onMounted, ref, watch, type Ref } from 'vue';

const emit = defineEmits([
  'update:status',
  'update:type',
  'update:method',
  'update:dateRange',
  'update:clearFilters',
  'update:searchTerm',
]);

const paymentStatus = ref<PaymentStatus | undefined>(undefined);
const paymentType = ref<PaymentType | undefined>(undefined);
const paymentMethod = ref<PaymentMethod | undefined>(undefined);
const searchTerm = ref('');
const startEndDates = ref({ start: undefined, end: undefined }) as Ref<DateRange>;

onMounted(async () => {
  watch(startEndDates, (newValue) => {
    onDateChange(newValue);
  });

  watch(searchTerm, (newValue) => {
    onSearch(newValue);
  });
});

const df = new DateFormatter('en-US', {
  dateStyle: 'medium',
});

const onUpdatePaymentStatus = (value: any) => {
  emit('update:status', value);
};

const onUpdatePaymentType = (value: any) => {
  emit('update:type', value);
};

const onUpdatePaymentMethod = (value: any) => {
  emit('update:method', value);
};

const onDateChange = (newValue: DateRange) => {
  if (newValue.start && newValue.end) {
    startEndDates.value = newValue;
    emit('update:dateRange', startEndDates.value);
  }
};

const onClearFilters = () => {
  paymentStatus.value = undefined;
  paymentType.value = undefined;
  paymentMethod.value = undefined;
  searchTerm.value = '';
  startEndDates.value = { start: undefined, end: undefined };
  emit('update:clearFilters');
};

const onSearch = debounce((search: string) => {
  emit('update:searchTerm', search);
}, 300);
</script>
